import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type ProductKind = Database["public"]["Enums"]["product_kind"];

export type StorefrontVariant = {
  id: string;
  name: string;
  price_cents: number;
  currency: string;
  billing_interval: Database["public"]["Enums"]["billing_interval"];
  stock_tracked: boolean;
  stock_qty: number;
  metadata: Record<string, any>;
  sort_order: number;
};

export type StorefrontProduct = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  kind: ProductKind;
  cover_url: string | null;
  featured: boolean;
  metadata: Record<string, any>;
  variants: StorefrontVariant[];
  from_cents: number | null;
  currency: string;
};

function shapeVariants(rows: any[]): StorefrontVariant[] {
  return (rows || [])
    .filter((v) => v.active !== false)
    .map((v) => ({
      id: v.id,
      name: v.name,
      price_cents: v.price_cents,
      currency: v.currency || "USD",
      billing_interval: v.billing_interval,
      stock_tracked: !!v.stock_tracked,
      stock_qty: v.stock_qty ?? 0,
      metadata: (v.metadata as Record<string, any>) || {},
      sort_order: v.sort_order ?? 0,
    }))
    .sort((a, b) => a.sort_order - b.sort_order || a.price_cents - b.price_cents);
}

function shapeProduct(p: any): StorefrontProduct {
  const variants = shapeVariants(p.product_variants || []);
  const from = variants.length ? variants[0].price_cents : null;
  return {
    id: p.id,
    slug: p.slug,
    name: p.name,
    description: p.description,
    kind: p.kind,
    cover_url: p.cover_url,
    featured: !!p.featured,
    metadata: (p.metadata as Record<string, any>) || {},
    variants,
    from_cents: from,
    currency: variants[0]?.currency || "USD",
  };
}

export async function fetchProductsByKind(kind: ProductKind): Promise<StorefrontProduct[]> {
  const { data, error } = await supabase
    .from("products")
    .select("*, product_variants(*)")
    .eq("kind", kind)
    .eq("active", true)
    .order("featured", { ascending: false })
    .order("name");
  if (error) throw error;
  return (data || []).map(shapeProduct);
}

export async function fetchProductBySlug(slug: string): Promise<StorefrontProduct | null> {
  const { data, error } = await supabase
    .from("products")
    .select("*, product_variants(*)")
    .eq("slug", slug)
    .eq("active", true)
    .maybeSingle();
  if (error) throw error;
  return data ? shapeProduct(data) : null;
}

export function formatPrice(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

export function intervalLabel(interval: StorefrontVariant["billing_interval"]) {
  switch (interval) {
    case "monthly": return "/mo";
    case "yearly": return "/yr";
    case "lifetime": return " lifetime";
    default: return "";
  }
}
